import * as SheetPrimitive from "@radix-ui/react-dialog";
import { History, Plus, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Thread } from "@/components/assistant-ui/thread";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { Sheet, SheetDescription, SheetPortal, SheetTitle } from "@/components/ui/sheet";
import { ThreadList } from "@/domains/ai/ai-chat/components/thread-list";
import { useAiThreadContext } from "@/domains/ai/ai-chat/contexts/ai-thread-context";
import { useAppUIStore } from "@/domains/app/stores/app-ui-store";
import { useConfig } from "@/hooks/use-config";
import { useSalonStore } from "@/lib/store/salon";
import { cn } from "@/lib/utils";

/**
 * AI Chat Mobile Sheet Component
 *
 * Renders a full-screen bottom sheet on mobile that contains the AI chat interface
 * Simplified version without gradient border for mobile
 * Only rendered on mobile devices
 *
 * Keyboard handling: Sheet height is reduced when keyboard shows using CSS variable
 * Note: Uses Sheet primitives directly to avoid the overlay background
 */
export function AIChatMobileSheet() {
  const { t } = useTranslation("ai-chat");
  const [view, setView] = useState<"chat" | "history">("chat");
  const { currentSalon } = useSalonStore();
  const { currentThreadId, resetThread, loadThread } = useAiThreadContext();

  const mobileAiSheetOpen = useAppUIStore((state) => state.mobileAiSheetOpen);
  const setMobileAiSheetOpen = useAppUIStore((state) => state.setMobileAiSheetOpen);

  // Check if AI chat is enabled for this salon
  const { value: aiChatEnabled = false } = useConfig("premium_features.ai_chat_enabled");

  // Prevent Safari from scrolling when sheet opens
  useEffect(() => {
    if (mobileAiSheetOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      // Prevent any scroll behavior
      const preventScroll = (e: Event) => {
        e.preventDefault();
        window.scrollTo(scrollX, scrollY);
      };

      // Lock scroll immediately
      window.scrollTo(scrollX, scrollY);
      document.addEventListener("scroll", preventScroll, { passive: false });

      // Also restore after animation completes
      const timeoutId = setTimeout(() => {
        window.scrollTo(scrollX, scrollY);
      }, 100);

      return () => {
        document.removeEventListener("scroll", preventScroll);
        clearTimeout(timeoutId);
      };
    }
  }, [mobileAiSheetOpen]);

  const handleSelectThread = async (threadId: string) => {
    setView("chat");
    try {
      await loadThread(threadId);
    } catch (error) {
      console.error(`[AIChatMobileSheet] Failed to load thread ${threadId}:`, error);
    }
  };

  const handleNewThread = () => {
    setView("chat");
    resetThread();
  };

  const handleClose = () => {
    setMobileAiSheetOpen(false);
  };

  if (!aiChatEnabled) return null;

  return (
    <Sheet open={mobileAiSheetOpen} onOpenChange={setMobileAiSheetOpen}>
      <SheetPortal>
        {/* Overlay to block background interaction */}
        <SheetPrimitive.Overlay className="fixed inset-0 z-40 bg-black/50" />

        <SheetPrimitive.Content
          className={cn(
            "fixed z-50 gap-0 shadow-lg transition ease-in-out",
            "data-[state=closed]:duration-300 data-[state=open]:duration-500",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "left-0 right-0 top-0 p-0",
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            "flex flex-col overflow-hidden",
            "bg-background",
            "[padding-bottom:0px!important]" // Force override any safe area padding
          )}
          style={{
            bottom: "0px",
            maxHeight: "calc(100vh - var(--keyboard-height, 0px))",
            height: "100vh",
          }}
        >
          {/* Accessibility - required by Radix but hidden visually */}
          <SheetTitle className="sr-only">AI Assistant</SheetTitle>
          <SheetDescription className="sr-only">Chat with AI assistant</SheetDescription>

          {/* Content - safe area padding for top only */}
          <div className="h-full flex flex-col overflow-hidden pt-safe-top">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <TooltipIconButton
                  variant="ghost"
                  tooltip={t("sidebar.close")}
                  side="right"
                  onClick={handleClose}
                  className="h-8 w-8"
                >
                  <X className="w-4 h-4" />
                </TooltipIconButton>
                <h2 className="font-semibold text-lg">
                  {view === "chat" ? t("sidebar.title") : t("sidebar.conversations")}
                </h2>
              </div>
              <div className="flex items-center gap-1">
                {/* Toggle view button */}
                <TooltipIconButton
                  variant="ghost"
                  tooltip={view === "chat" ? t("sidebar.viewHistory") : t("sidebar.currentChat")}
                  side="left"
                  onClick={() => setView(view === "chat" ? "history" : "chat")}
                  className="h-8 w-8"
                >
                  <History className="w-4 h-4" />
                </TooltipIconButton>
                {/* New conversation button */}
                <button
                  type="button"
                  onClick={handleNewThread}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t("sidebar.new")}</span>
                </button>
              </div>
            </div>

            {/* Content - bottom padding for safe area */}
            <div className="flex-1 overflow-hidden pb-safe-bottom">
              {view === "chat" ? (
                <Thread />
              ) : (
                currentSalon && (
                  <ThreadList
                    salonId={currentSalon.id}
                    onSelectThread={handleSelectThread}
                    onNewThread={handleNewThread}
                    currentThreadId={currentThreadId || undefined}
                  />
                )
              )}
            </div>
          </div>
        </SheetPrimitive.Content>
      </SheetPortal>
    </Sheet>
  );
}
